import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import i18n from "i18next";
import Cookies from "js-cookie";

import { LinkContext } from "../LinkProvider";
import logoIcon from "../img/logo.png";
import MobileMenu from "./MobileMenu";
import LangSwitcher from "./LangSwitcher";

function Header() {
  const { link } = useContext(LinkContext);
  const { t } = useTranslation();
  const location = useLocation();

  const languages = [
    { value: "en", label: "EN", name: "English" },
    { value: "de", label: "DE", name: "Deutsch" },
    { value: "es", label: "ES", name: "Español" },
    { value: "fr", label: "FR", name: "Français" },
  ];

  const storedLang = Cookies.get("i18next");
  const [currentLang, setCurrentLang] = useState(storedLang || "en");
  const [wipe, setWipe] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const lastY = useRef(0);
  const wipeTimers = useRef([]);

  useEffect(() => {
    if (storedLang && storedLang !== currentLang) {
      setCurrentLang(storedLang);
      i18n.changeLanguage(storedLang);
    }
    document.documentElement.lang = currentLang;
    document.body.setAttribute("dir", storedLang === "ar" ? "rtl" : "ltr");
  }, [storedLang, currentLang]);

  useEffect(() => {
    const root = document.getElementById("root");
    if (!root) return;
    root.classList.toggle("lang-ru", currentLang === "ru");
    root.classList.toggle("lang-pl", currentLang === "pl");
  }, [currentLang]);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 20);
      setHidden(y > lastY.current && y > 140);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => () => wipeTimers.current.forEach(clearTimeout), []);

  const applyLang = (value) => {
    setCurrentLang(value);
    i18n.changeLanguage(value);
    Cookies.set("i18next", value);
  };

  const handleChangeLang = (selectedLang) => {
    if (selectedLang.value === currentLang) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      applyLang(selectedLang.value);
      setMenuOpen(false);
      return;
    }

    setWipe(selectedLang);
    wipeTimers.current.forEach(clearTimeout);
    wipeTimers.current = [
      setTimeout(() => {
        applyLang(selectedLang.value);
        setMenuOpen(false);
      }, 500),
      setTimeout(() => setWipe(null), 1050),
    ];
  };

  const navLinks = [
    { to: "/markets", label: t("header.1") },
    { to: "/education", label: t("header.2") },
    { to: "/trade", label: t("header.3") },
    { to: "/account", label: t("header.4") },
    { to: "/about", label: t("header.5") },
  ];

  return (
    <header
      className="fixed top-0 left-0 right-0 z-[100]"
      style={{
        // transform:none, чтобы fixed-элементы моб. меню внутри header позиционировались от viewport
        transform: hidden && !menuOpen && !wipe ? "translateY(-100%)" : "none",
        backgroundColor: scrolled && !menuOpen ? "rgba(2,2,2,0.92)" : "#020202",
        boxShadow: scrolled && !menuOpen && !hidden ? "0 10px 30px rgba(0,0,0,0.35)" : "none",
        borderBottom: scrolled && !menuOpen ? "1px solid rgba(255,255,255,0.06)" : "1px solid transparent",
        transition:
          "transform 0.4s cubic-bezier(0.22,1,0.36,1), background-color 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
      }}
    >
      <div className="hidden xl:grid max-w-[1220px] mx-auto grid-cols-[auto_1fr_auto] items-center gap-[30px] py-[16px]">
        <Link to="/" className="shrink-0">
          <img src={logoIcon} alt="Treefield Partners AG" className="w-[36px] h-[36px] object-contain" />
        </Link>

        <nav className="flex items-center justify-center gap-[30px]">
          {navLinks.map((item) => (
            <Link key={item.to} to={item.to} className="group">
              <span
                className="relative inline-block font-['Inter'] font-medium text-[14px] tracking-[1.4px] uppercase whitespace-nowrap transition-colors duration-200 after:absolute after:left-0 after:-bottom-[5px] after:h-[1.5px] after:w-full after:origin-right after:scale-x-0 after:bg-[#ee5414] after:transition-transform after:duration-300 after:ease-out group-hover:after:origin-left group-hover:after:scale-x-100"
                style={{ color: location.pathname === item.to ? "#fff" : "#636363" }}
              >
                {item.label}
              </span>
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-[30px] shrink-0">
          <LangSwitcher
            languages={languages}
            currentLang={currentLang}
            onChange={handleChangeLang}
          />
          <a
            href={link}
            className="flex items-center justify-center h-[36px] min-w-[107px] px-[20px] whitespace-nowrap rounded-[60px] bg-[#ee5414] font-['Inter'] font-medium text-[16px] text-white uppercase hover:opacity-90 transition-opacity cursor-my"
          >
            {t("header.6")}
          </a>
        </div>
      </div>

      <div className="xl:hidden flex items-center justify-between py-[12px] px-[20px]">
        <Link to="/" className="shrink-0">
          <img src={logoIcon} alt="Treefield Partners AG" className="w-[32px] h-[32px] object-contain" />
        </Link>
        <div className="flex items-center gap-[16px]">
          <MobileMenu
            isOpen={menuOpen}
            setIsOpen={setMenuOpen}
            navLinks={navLinks}
            link={link}
            t={t}
            logoIcon={logoIcon}
            languages={languages}
            currentLang={currentLang}
            onChangeLang={handleChangeLang}
          />
        </div>
      </div>

      {/* Шторка при смене языка */}
      <AnimatePresence>
        {wipe && (
          <motion.div
            className="fixed inset-0 z-[200] flex items-center justify-center bg-[#ee5414]"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "-100%" }}
            transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
          >
            <div
              className="absolute inset-0 banner-grid-lines opacity-30 pointer-events-none"
              aria-hidden="true"
            />
            <motion.div
              className="relative flex flex-col items-center gap-[10px]"
              initial={{ opacity: 0, y: 36 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -28 }}
              transition={{ delay: 0.16, duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="font-['Montserrat'] font-[900] uppercase leading-none text-white text-[40px] sm:text-[64px] tracking-[-1.28px]">
                {wipe.name}
              </span>
              <span className="font-['Inter'] font-medium text-[14px] tracking-[4px] uppercase text-[rgba(255,255,255,0.75)]">
                {wipe.label}
              </span>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

export default Header;
